/* eslint-disable jsx-a11y/anchor-is-valid */
import { SelectionChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react';
import { NumberFormatValues, SourceInfo } from 'react-number-format';
import Header from '../../../common/Header/Header';
import { H2 } from '../../../common/Headings/Headings.styles';
import Button from '../../../components/Button/Button';
import { Variant } from '../../../components/Button/Button.types';
import Dropdown from '../../../components/Dropdown/Dropdown';
import { EditCellRenderer } from '../../../components/Table/EditCellRenderer/EditCellRenderer';
import Table from '../../../components/Table/Table';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { useGetSubRecipeBases } from '../../../services/base/base.service';
import { Base } from '../../../services/base/base.types';
import { useGetClasses } from '../../../services/class/class.service';
import { Class } from '../../../services/class/class.types';
import { useGetSubRecipeOptions } from '../../../services/option/option.service';
import { Option } from '../../../services/option/option.types';
import { useGetSubRecipeProducts } from '../../../services/product/product.service';
import { Product } from '../../../services/product/product.types';
import { useSubRecipeDetailRawProducts } from '../../../services/rawProd/rawProd.service';
import { RawProd } from '../../../services/rawProd/rawProd.types';
import { useGetSubRecipeSizes } from '../../../services/size/size.service';
import { Size } from '../../../services/size/size.types';
import { useGetSpecs } from '../../../services/spec/spec.service';
import {
  useEditBulkSubrecipeDetailsUsageAmt,
  useEditSubrecipeDetailUsageAmt,
  useSubrecipeDetailsUsageAmt,
  useSubrecipeDetailsUsageAmtOptions,
} from '../../../services/subrecipe/subrecipe.service';
import { SubrecipeRawProduct, UsageAmt } from '../../../services/subrecipe/subrecipe.types';
import { useFetchErrorToast } from '../../../utils/useFetchErrorToasts/useFetchErrorToast';
import MultiUpdateUsageAmtModal from '../MultiUpdateUsageAmtModal/MultiUpdateUsageAmtModal';
import { DropdownWrapper, Page, RowWrapper } from './ChangeUsage.styles';
import { DropdownWrapperMedium } from '../../../common/FormElementWrappers/FormElementWrappers.styles';
import { OPTIONS_PLACEHOLDER } from '../../../constants/Dropdown';

const ChangeUsage: FC = () => {
  const availableGridRef = useRef<AgGridReact>(null);
  const [selectedSpecs, setSelectedSpecs] = useState<any>([]);
  const [rawProductValue, setRawProductValue] = useState<any>(null);
  const [selectedUsageAmount, setSelectedUsageAmount] =useState<any>(null);
  const [subrecipeDetailsTableData, setSubrecipeDetailsTableData] = useState<any>([]);
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false);
  const [selectedSpecNo, setSelectedSpecNo] = useState<string>('');
  const [selectedSubSpecNo, setSelectedSubSpecNo] = useState<string>('');
  const [modalErrorText, setModalErrorText] = useState<string>('');
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [multiUsageAmountValue, setMultiUsageAmountValue] = useState<string>('');
  const [activeSpecSearch, setActiveSpecSearch] = useState<boolean>(true)
  const [specClass, setSpecClass] = useState<number | undefined>(undefined);
  const { data: specsData, isError: specsError } = useGetSpecs({
    isSubRecipe: true,
    classNo: specClass
  }, activeSpecSearch);
  const { mutate: editSubrecipeDetail } = useEditSubrecipeDetailUsageAmt(selectedSpecNo, selectedSubSpecNo);
  const { mutate: editBulkSubrecipeDetails } = useEditBulkSubrecipeDetailsUsageAmt();

  const filteredSpecs = selectedSpecs.map((spec: any) => {
    return spec.specNo;
  });

  // Fetch classes and map to dropdown options
  const { data: classes, isError: classesError } = useGetClasses();
  const [selectedClass, setSelectedClass] = useState<number | undefined>(undefined);
  const classOptions =
    classes?.data?.map((item: Class) => {
      return { value: item?.classNo?.toString(), label: `${item?.classNo} ~ ${item?.description}` };
    }) || [];

  const filteredClassOptions = (classOptions: any[]) => {
    const classList = selectedSpecs.flatMap((spec: any) => {
      return spec.classNos
    });

    const uniqueClassList: Set<number> = new Set(classList);

    return classOptions.filter(classOption => {
      const classNoInteger = parseInt(classOption.value);
      return uniqueClassList.has(classNoInteger) || classNoInteger === 999;
    });
  }

  // Fetch bases and map to dropdown options
  const { data: bases, isError: isBasesError, error: basesError } = useGetSubRecipeBases(
    { classNo: selectedClass?.toString() || '' },
    selectedClass !== undefined
  );
  const [selectedBase, setSelectedBase] = useState<number | null>(null);
  const baseOptions =
    bases?.data?.map((item: Base) => {
      return { value: item?.baseNo?.toString(), label: `${item?.baseNo} ~ ${item?.description}` };
    }) || [];

  // Fetch sizes and map to dropdown options
  const { data: sizes, isError: isSizesError, error: sizesError } = useGetSubRecipeSizes(
    { classNo: selectedClass?.toString() || '', baseNo: selectedBase?.toString() || '' },
    selectedClass !== undefined
  );
  const [selectedSize, setSelectedSize] = useState<number | null>(null);
  const sizeOptions =
    sizes?.data?.map((item: Size) => {
      return { value: item?.sizeNo?.toString(), label: `${item?.sizeNo} ~ ${item?.description}` };
    }) || [];

  // Fetch products and map to dropdown options
  const { data: products, isError: isProductsError, error: productsError } = useGetSubRecipeProducts(
    {
      classNo: selectedClass?.toString() || '',
      baseNo: selectedBase?.toString() || '',
      sizeNo: selectedSize?.toString() || '',
    },
    selectedClass !== undefined
  );
  const [selectedProduct, setSelectedProduct] = useState<number | null>(null);
  const productOptions =
    products?.data?.map((item: Product) => {
      return { value: item?.productNo?.toString(), label: `${item?.productNo} ~ ${item?.description}` };
    }) || [];

  // Fetch options and map to dropdown options
  const { data: options, isError: isOptionsError, error: optionsError } = useGetSubRecipeOptions(
    {
      classNo: selectedClass?.toString() || '',
      baseNo: selectedBase?.toString() || '',
      sizeNo: selectedSize?.toString() || '',
      productNo: selectedProduct?.toString() || '',
    },
    selectedClass !== undefined
  );
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const optionOptions =
    options?.data?.map((item: Option) => {
      return { value: item?.optionNo?.toString(), label: `${item?.optionNo} ~ ${item?.description}` };
    }) || [];

  // Fetch raw products and map to dropdown options
  const { data: subRecipeDetailRawProductsResponse, isError: subRecipeDetailRawProductsIsError, error: subRecipeDetailRawProductsError } = useSubRecipeDetailRawProducts(
    {
      specs: filteredSpecs,
      classNo: selectedClass,
      baseNo: selectedBase,
      sizeNo: selectedSize,
      prodNo: selectedProduct,
      optionNo: selectedOption,
    },
    selectedClass !== undefined && filteredSpecs > 0
  );
  const rawProductOptions =
    subRecipeDetailRawProductsResponse?.data?.map((item: RawProd) => {
      return { value: item?.moaRawProdNo?.toString(), label: `${item?.moaRawProdNo} ~ ${item?.moaRawProdDesc}` };
    }) || [];

    const{data: subrecipeDetailsUsageAmtOptions, isError: subrecipeDetailsUsageAmtOptionsError, refetch: refetchUsageAmount} = useSubrecipeDetailsUsageAmtOptions(
      {
      specNos: filteredSpecs,
      moaClassNo: selectedClass,
      moaBaseNo: selectedBase,
      moaSizeNo: selectedSize,
      moaProdNo: selectedProduct,
      moaOptionNo: selectedOption,
      moaRawProdNo: rawProductValue,
      usageAmount: selectedUsageAmount,
      },
      selectedClass !== undefined && filteredSpecs?.length > 0 ,
    );

    const usageAmoutOptions = 
      subrecipeDetailsUsageAmtOptions?.data?.map((item: UsageAmt) => {
        return {value: item?.usageAmount?.toString(), label: `${item.usageAmount}`};
      }) || [];

  const {
    data: subrecipeDetailsData,
    isError: subRecipeDetailsError,
    isLoading,
    refetch,
    remove,
  } = useSubrecipeDetailsUsageAmt(
    {
      specNos: filteredSpecs,
      moaClassNo: selectedClass,
      moaBaseNo: selectedBase,
      moaSizeNo: selectedSize,
      moaProdNo: selectedProduct,
      moaOptionNo: selectedOption,
      moaRawProdNo: rawProductValue,
      usageAmount: selectedUsageAmount,
    },
    isSearchActive
  );

  const isDisabled = filteredSpecs?.length === 0 || selectedClass === undefined;

  useFetchErrorToast([
    { isError: specsError, label: 'Error fetching specs', id: 'specsFetchError' },
    { isError: classesError, label: 'Error fetching classes', id: 'classFetchError' },
    { isError: isBasesError, label: 'Error fetching bases', id: 'baseFetchError', ignoredErrors: ["Request failed with status code 404"], error: basesError },
    { isError: isSizesError, label: 'Error fetching sizes', id: 'sizeFetchError', ignoredErrors: ["Request failed with status code 404"], error: sizesError },
    { isError: isProductsError, label: 'Error fetching products', id: 'productFetchError', ignoredErrors: ["Request failed with status code 404"], error: productsError },
    { isError: isOptionsError, label: 'Error fetching options', id: 'optionFetchError', ignoredErrors: ["Request failed with status code 404"], error: optionsError },
    {
      isError: subRecipeDetailRawProductsIsError,
      label: 'Error fetching raw products in subrecipe',
      id: 'subRecipeDetailRawProductsError',
      ignoredErrors: ["Request failed with status code 404"],
      error: subRecipeDetailRawProductsError
    },
    {
      isError: subRecipeDetailsError,
      label: 'Error fetching subrecipe details',
      id: 'subrecipeDetailsError',
    },
    {
      isError: subrecipeDetailsUsageAmtOptionsError,
      label: 'Error fetching usage amount options',
      id: 'usageAmountOptionsError'
    },
  ]);

  const handleSelectionChangedAvailable = (event: SelectionChangedEvent) => {
    setIsSearchActive(false);
    setSelectedSpecs(event.api.getSelectedRows());
    remove();
  };

  const handleClassSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setIsSearchActive(false);
    setSelectedClass(+event.target.value);
    setSelectedBase(null);
    setSelectedSize(null);
    setSelectedProduct(null);
    setSelectedOption(null);
    setRawProductValue(null);
    remove();
  };

  const handleBaseSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setIsSearchActive(false);
    setSelectedBase(+event.target.value);
    setSelectedSize(null);
    setSelectedProduct(null);
    setSelectedOption(null);
    setRawProductValue(null);
    remove();
    if (event.target.value === 'null') {
      setSelectedBase(null);
    }
  };

  const handleSizeSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setIsSearchActive(false);
    setSelectedSize(+event.target.value);
    setSelectedProduct(null);
    setSelectedOption(null);
    setRawProductValue(null);
    remove();
    if (event.target.value === 'null') {
      setSelectedSize(null);
    }
  };

  const handleProductSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setIsSearchActive(false);
    setSelectedProduct(+event.target.value);
    setSelectedOption(null);
    setRawProductValue(null);
    remove();
    if (event.target.value === 'null') {
      setSelectedProduct(null);
    }
  };

  const handleOptionSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setIsSearchActive(false);
    setSelectedOption(+event.target.value);
    setRawProductValue(null);
    remove();
    if (event.target.value === 'null') {
      setSelectedOption(null);
    }
  };

  const handleRawProductSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setIsSearchActive(false);
    setRawProductValue(+event.target.value);
    remove();
    if (event.target.value === 'null') {
      setRawProductValue(null);
    }
  };
  const handleUsageAmountSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setIsSearchActive(false);
    setSelectedUsageAmount(+event.target.value);
    remove();
    if (event.target.value === 'null') {
      setRawProductValue(null);
    }
  };

  const handleSearchSubrecipeDetails = () => {
    setIsSearchActive(true);
    setSubrecipeDetailsTableData(subrecipeDetailsData?.data);
  };

  useEffect(() => {
    if (subrecipeDetailsData) {
      setSubrecipeDetailsTableData(subrecipeDetailsData?.data);
    }
  }, [subrecipeDetailsData]);

  useEffect(() => {
      setActiveSpecSearch(true);
  }, [specClass])

  const handleCellEdit = useCallback(    (event: any) => {
      let newValue = +event.newValue;
      if (newValue > 9999.9999) newValue = 9999.9999;
      if (newValue < -9999.9999) newValue = -9999.9999;
      if (Number.isNaN(newValue)) newValue = event.oldValue;
      const newSubrecipeDetailsTableData = subrecipeDetailsData?.data.map((item: any) => {
        if (item.moaRawProdNo === event.data.moaRawProdNo) {
          setSelectedSpecNo(event.data.specNo.toString());
          setSelectedSubSpecNo(event.data.subSpecNo.toString());
          return { ...item, usageAmount: newValue };
        } else {
          return item;
        }
      });
      if (newValue === event.oldValue) {
        showToast({
          label: 'Please input a numerical value.',
          id: 'changeUsageAmountSuccess',
          toastType: ToastTypes.Error,
        });
      } else {
        editSubrecipeDetail(
          {
            oldRawProdNo: event.data.moaRawProdNo,
            usageAmount: newValue,
          },
          {
            onSuccess: () => {
              refetch();
              refetchUsageAmount()
              showToast({
                label: 'Usage amount updated successfully.',
                id: 'changeUsageAmountSuccess',
              });
            },
            onError: (error) => {
              showToast({
                label: 'Error updating usage amount.',
                id: 'changeUsageAmountError',
                toastType: ToastTypes.Error,
              });
            },
          }
        );
      }
      setSubrecipeDetailsTableData(newSubrecipeDetailsTableData);
    },
    [subrecipeDetailsData]
  );

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalErrorText('');
    setMultiUsageAmountValue('');
  };

  const handleMultiUsageInputChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    setModalErrorText('');
    setMultiUsageAmountValue(values.value);
  };

  const handleSubmit = () => {
    let searchedSubrecipes =
      subrecipeDetailsData?.data?.map((record: SubrecipeRawProduct) => {
        return {
          specNo: record.specNo,
          subSpecNo: record.subSpecNo,
          moaRawProdNo: record.moaRawProdNo,
        };
      }) || [];
    if (multiUsageAmountValue.length < 1) {
      setModalErrorText('Please input a usage amount');
    } else if (Number(multiUsageAmountValue) < -9999.9999 || Number(multiUsageAmountValue) > 9999.9999) {
      setModalErrorText('Please input a valid usage amount');
    } else {
      editBulkSubrecipeDetails(
        {
          keys: searchedSubrecipes,
          usageAmount: +multiUsageAmountValue,
        },
        {
          onSuccess: () => {
            refetch();
            handleCloseModal();
            showToast({
              label: 'Usage amounts updated successfully.',
              id: 'changeBulkUsageAmountsSuccess',
              toastType: ToastTypes.Success,
            });
          },
          onError: () => {
            showToast({
              label: 'Error updating usage amounts.',
              id: 'changeBulkUsageAmountsError',
              toastType: ToastTypes.Error,
            });
          },
        }
      );
    }
  };

  const handleClearFilter = () => {
    setIsSearchActive(false);
    setActiveSpecSearch(false);
    setSpecClass(undefined);
  };
  const handleSelectSpecClass = (event: ChangeEvent<HTMLSelectElement>) => {
    setActiveSpecSearch(false);
    if(+event.target.value === 999){
      setSpecClass(undefined)
    } else {
      setSpecClass(+event?.target.value)
    }
    setIsSearchActive(false);
  };

  const onFirstDataRendered = () => {
    const inputElement = document.querySelector(
      'input.ag-input-field-input.ag-text-field-input'
    ) as HTMLInputElement;
    inputElement?.focus();
  };

  return (
    <>
      <Header breadcrumb={{ to: '/optionrecipes', label: 'Back to Option Recipes' }} title='Change Usage Amounts' />
      <Page>
        <H2>Select specs to edit</H2>
        <RowWrapper>
          <DropdownWrapperMedium>
            <Dropdown
                id='selectedClass'
                label='Class'
                options={classOptions}
                value={specClass ? specClass.toString() : OPTIONS_PLACEHOLDER}
                onChange={handleSelectSpecClass}
                optionsPlaceholder='Select Class'
                dataTestId={'FilterSpecDropdown'}
              />
          </DropdownWrapperMedium>
          <Button
            label='Remove Filter'
            variant={Variant.Secondary}
            onClick={handleClearFilter}
          />
        </RowWrapper>
        <Table
          ref={availableGridRef}
          showHeader={true}
          headerTitle='Available Specs'
          onFirstDataRendered={onFirstDataRendered}
          columnDefs={[
            {
              minWidth: 70,
              maxWidth: 70,
              flex: 1,
              headerName: 'Select',
              checkboxSelection: true,
              floatingFilter: false,
              sortable: false,
            },
            { minWidth: 100, flex: 1, field: 'specNo', headerName: 'Spec Number', cellRenderer: 'agGroupCellRenderer', filter: 'agTextColumnFilter' },
            { minWidth: 120, flex: 3, field: 'description', headerName: 'Description', resizable: false },
          ]}
          rowSelection='multiple'
          rowData={specsData?.data || []}
          returnedRecords={specsData?.data?.length || 0}
          onSelectionChanged={handleSelectionChangedAvailable}
          height={'600px'}
          width={'100%'}
        />
        <H2>Filter and edit records</H2>
        <RowWrapper>
          <DropdownWrapper>
            <Dropdown
              id='moaClassNo'
              label='Class'
              options={filteredClassOptions(classOptions)}
              value={selectedClass?.toString()}
              onChange={handleClassSelect}
              optionsPlaceholder='Select Class'
              disabled={filteredSpecs?.length === 0}
              dataTestId={'FilterSubRecipeDetailDropdown'}
            />
          </DropdownWrapper>
          <DropdownWrapper>
            <Dropdown
              id='moaBaseNo'
              label='Base'
              options={[{ value: 'null', label: '' }, ...baseOptions]}
              value={selectedBase?.toString() ?? 'null'}
              onChange={handleBaseSelect}
              hidePlaceholder={true}
              disabled={isDisabled}
              dataTestId={'BaseDropdown'}
            />
          </DropdownWrapper>
          <DropdownWrapper>
            <Dropdown
              id='moaSizeNo'
              label='Size'
              options={[{ value: 'null', label: '' }, ...sizeOptions]}
              value={selectedSize?.toString() ?? 'null'}
              onChange={handleSizeSelect}
              hidePlaceholder={true}
              disabled={isDisabled}
              dataTestId={'SizeDropdown'}
            />
          </DropdownWrapper>
          <DropdownWrapper>
            <Dropdown
              id='moaProdNo'
              label='Product'
              options={[{ value: 'null', label: '' }, ...productOptions]}
              value={selectedProduct?.toString() ?? 'null'}
              onChange={handleProductSelect}
              hidePlaceholder={true}
              disabled={isDisabled}
              dataTestId={'ProductDropdown'}
            />
          </DropdownWrapper>
          <DropdownWrapper>
            <Dropdown
              id='moaOptionNo'
              label='Option'
              options={[{ value: 'null', label: '' }, ...optionOptions]}
              value={selectedOption?.toString() ?? 'null'}
              onChange={handleOptionSelect}
              hidePlaceholder={true}
              disabled={isDisabled}
              dataTestId={'OptionDropdown'}
            />
          </DropdownWrapper>
          <DropdownWrapper>
            <Dropdown
              id='moaRawProdNo'
              label='Raw Product'
              options={[{ value: 'null', label: '' }, ...rawProductOptions]}
              value={rawProductValue?.toString() ?? 'null'}
              onChange={handleRawProductSelect}
              hidePlaceholder={true}
              disabled={isDisabled}
              dataTestId={'RawProductDropdown'}
            />
          </DropdownWrapper>
          <DropdownWrapper>
            <Dropdown
              id='usageAmount'
              label='Usage Amount'
              options={[{ value: 'null', label: '' }, ...usageAmoutOptions]}
              value={selectedUsageAmount?.toString() ?? 'null'}
              onChange={handleUsageAmountSelect}
              hidePlaceholder={true}
              disabled={isDisabled}
              dataTestId={'UsageAmountDropdown'}
            />
          </DropdownWrapper>
          <Button
            label='Search'
            variant={Variant.Secondary}
            onClick={handleSearchSubrecipeDetails}
            disabled={isDisabled}
            isLoading={isLoading}
            loadingLabel={'Searching...'}
          />
        </RowWrapper>
        <Table
          showHeader={false}
          columnDefs={[
            { minWidth: 120, flex: 1, field: 'classDescription', headerName: 'Class', floatingFilter: false },
            { minWidth: 120, flex: 1, field: 'baseDescription', headerName: 'Base', floatingFilter: false },
            { minWidth: 120, flex: 1, field: 'sizeDescription', headerName: 'Size', floatingFilter: false },
            { minWidth: 120, flex: 1, field: 'prodDescription', headerName: 'Product', floatingFilter: false },
            { minWidth: 120, flex: 1, field: 'optionDescription', headerName: 'Option', floatingFilter: false },
            { minWidth: 120, flex: 1, field: 'specNo', headerName: 'Spec Number', floatingFilter: false },
            {
              minWidth: 120,
              flex: 1,
              field: 'rawProdDescription',
              headerName: 'Raw Product',
              floatingFilter: false,
            },
            {
              minWidth: 120,
              flex: 1,
              field: 'usageAmount',
              headerName: 'Usage Amount',
              floatingFilter: false,
              resizable: false,
              editable: true,
              cellRenderer: EditCellRenderer,
            },
          ]}
          footerButtons={[
            {
              label: 'Multi Update',
              variant: Variant.Secondary,
              onClick: handleOpenModal,
              disabled: subrecipeDetailsTableData?.length === 0,
            },
          ]}
          rowData={subrecipeDetailsTableData}
          returnedRecords={subrecipeDetailsTableData?.length || 0}
          onCellEditRequest={handleCellEdit}
          readOnlyEdit={true}
        />
        <MultiUpdateUsageAmtModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSubmit={handleSubmit}
          multiUsageAmountValue={multiUsageAmountValue}
          handleChange={handleMultiUsageInputChange}
          errorText={modalErrorText}
        />
      </Page>
    </>
  );
};

export default ChangeUsage;
